import {
  ApplePayPaymentMethodOptions,
  CardPaymentMethodOptions,
  GooglePayPaymentMethodOptions,
  PaymentMethodOptionItem,
  PaypalPaymentMethodOptions,
} from '@mangopay/checkout-sdk-react-elements';
import {
  CheckoutSdkHostEventType,
  AuthorizeApplePayResult,
  AuthorizePaymentInput,
  CreateCardPayment,
  ApplePayPaymentRequests,
  ValidateApplePayMerchant,
  CreateApplePayPayIn,
  CreateCardRegistration,
  CreateGooglePayPayment,
  CreatePaypalPayment,
  PaymentMethodType,
  DeactivateSavedCard,
} from '@mangopay/checkout-sdk-core';
import { ApplePayPaymentRequest, checkCanMakePaymentsWithApplePay } from '../payment-methods';
import { AppEventsDispatcher } from '../proxy';

export interface StoredCallbacks {
  onCreateCardRegistration: CreateCardRegistration;
  onDeactivateSavedCard: DeactivateSavedCard;
  onCreateCardPayment: CreateCardPayment;
  onValidateApplePayMerchant: ValidateApplePayMerchant;
  onCreateApplePayPayIn: CreateApplePayPayIn;
  onCreatePaypalPayment: CreatePaypalPayment;
  onCreateGooglePayPayment: CreateGooglePayPayment;
}

export class PaymentMethodsService {
  private readonly paymentMethods: PaymentMethodOptionItem[];

  private storedCallbacks = {} as StoredCallbacks;

  private dispatchMessage: AppEventsDispatcher['dispatchMessage'];

  public getPaymentMethods() {
    return this.paymentMethods;
  }

  constructor(dispatchMessage: AppEventsDispatcher['dispatchMessage'], paymentMethods: PaymentMethodOptionItem[] = []) {
    const supportedPaymentMethods = this.getSupportedPaymentMethods(paymentMethods);
    if (!supportedPaymentMethods?.length) {
      throw new Error('At least one payment method is required in options.paymentMethods');
    }
    this.dispatchMessage = dispatchMessage;
    this.paymentMethods = supportedPaymentMethods;

    const cardOptions = this.getPaymentMethodOptions('card') as CardPaymentMethodOptions;
    this.storedCallbacks.onCreateCardRegistration = cardOptions?.onCreateCardRegistration;
    delete cardOptions?.onCreateCardRegistration;
    this.storedCallbacks.onCreateCardPayment = cardOptions?.onCreatePayment;
    delete cardOptions?.onCreatePayment;
    this.storedCallbacks.onDeactivateSavedCard = cardOptions?.onDeactivateSavedCard;
    delete cardOptions?.onDeactivateSavedCard;

    const applePayOption = this.getPaymentMethodOptions('apple_pay') as ApplePayPaymentMethodOptions;
    this.storedCallbacks.onValidateApplePayMerchant = applePayOption?.paymentRequest.onValidateMerchant;
    this.storedCallbacks.onCreateApplePayPayIn = applePayOption?.onCreatePayment;
    delete applePayOption?.paymentRequest?.onValidateMerchant;
    delete applePayOption?.onCreatePayment;

    const payPalOptions = this.getPaymentMethodOptions('paypal') as PaypalPaymentMethodOptions;
    this.storedCallbacks.onCreatePaypalPayment = payPalOptions?.onCreatePayment;
    delete payPalOptions?.onCreatePayment;

    const googlePayOptions = this.getPaymentMethodOptions('google_pay') as GooglePayPaymentMethodOptions;
    this.storedCallbacks.onCreateGooglePayPayment = googlePayOptions?.onCreatePayment;
    delete googlePayOptions?.onCreatePayment;
  }

  private getPaymentMethodOptions(type: PaymentMethodType) {
    return this.paymentMethods.find((method) => method.type === type)?.options;
  }

  private getApplePayRequest(): ApplePayPaymentRequests | undefined {
    const applePayPaymentMethodOption = this.getPaymentMethodOptions('apple_pay') as ApplePayPaymentMethodOptions;
    if (applePayPaymentMethodOption?.paymentRequest) {
      return applePayPaymentMethodOption?.paymentRequest;
    }
    return undefined;
  }

  getCallbackFn(fnName: keyof StoredCallbacks): unknown {
    return this.storedCallbacks[fnName];
  }

  authorizeApplePayPaymentRequest(authorizeRequestInput: AuthorizePaymentInput): Promise<AuthorizeApplePayResult> {
    return new Promise<AuthorizeApplePayResult>((resolve) => {
      this.dispatchMessage(CheckoutSdkHostEventType.LogHostEvent, {
        type: 'APPLE_PAY_AUTHORIZE_REQUEST_STARTED',
        data: authorizeRequestInput,
      });
      const applePayRequest = this.getApplePayRequest();

      if (!applePayRequest) {
        resolve({
          Status: 'ERROR',
          ResultMessage: 'PaymentRequest option is required',
        });
      }

      let request: ApplePayPaymentRequest;
      try {
        request = new ApplePayPaymentRequest(
          authorizeRequestInput,
          applePayRequest as ApplePayPaymentRequests,
          this.storedCallbacks.onValidateApplePayMerchant
        );
        request.onComplete = (result: AuthorizeApplePayResult) => {
          this.dispatchMessage(CheckoutSdkHostEventType.LogHostEvent, {
            type: 'APPLE_PAY_AUTHORIZE_REQUEST_COMPLETED',
            data: result,
          });
          return resolve(result);
        };
        request.onFailed = (event) => {
          this.dispatchMessage(CheckoutSdkHostEventType.LogHostEvent, {
            type: 'APPLE_PAY_AUTHORIZE_REQUEST_FAILED',
            data: event?.sessionError,
          });
          resolve({
            Status: 'ERROR',
            ResultMessage: 'Apple Pay request failed',
          });
        };

        request.onCancel = () => {
          resolve({
            Status: 'CANCELED',
            ResultMessage: 'Apple Pay request cancelled',
          });
        };

        request.start();
      } catch (error) {
        if (error instanceof Error) {
          resolve({
            Status: 'ERROR',
            ResultMessage: error.message || 'error.unknown',
          });
        }
        console.error(error);
      }
    });
  }

  private getSupportedPaymentMethods(methods: PaymentMethodOptionItem[]): PaymentMethodOptionItem[] | undefined {
    return methods.filter((method) => {
      switch (method.type) {
        case 'apple_pay':
          return checkCanMakePaymentsWithApplePay();
        default:
          return true;
      }
    });
  }
}
